import React, { ReactElement } from "react";
import { useGetAgentsQuery, User } from "../../../__generated__/api";
import Avatar from "../../avatar/Avatar";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface AgentPickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    className?: string;
    isClearable?: TIsClearable;
    exclude?: string[];
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never),
        agents: (TIsMulti extends true ? User[] : User) | (TIsClearable extends true ? undefined : never)
    ) => void;
}

const AgentPicker = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, className, isClearable, exclude,
}: AgentPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetAgentsQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<User>[] : Option<User>;
    type ValueType = TIsMulti extends true ? Option<User>[] : Option<User>;

    const handleSelected = (selected: SelectedType) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<User>).value) as Parameters<typeof onSelected>[0];
        const agents = (Array.isArray(selected)
            ? selected.map(row => row.data as User)
            : (selected as Option<User>).data) as Parameters<typeof onSelected>[1];
        onSelected(values, agents);
    };

    const excludeSet = new Set(exclude ?? []);
    const values = data?.settings?.agents?.filter(row => !excludeSet.has(row.id)).map(agent => ({
        label: (
            <>
                <Avatar src={agent.avatarUrl as string} variant="xs" />
                {agent.name}
            </>
        ),
        value: agent.id.toString() as string,
        data: agent as User,
    })) ?? [];
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    return (
        <Select
            ref={ref}
            filterOption={(user, input) => (input
                ? user.name?.toLowerCase().includes(input.toLowerCase())
                : true) ?? true}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={values ?? []}
            value={selected as ValueType}
            className={className}
            isClearable={isClearable}

        />
    );
});

AgentPicker.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    className: undefined,
    isClearable: undefined,
    exclude: [],
};

export default AgentPicker as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false
> (
    props: AgentPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof AgentPicker>;
